import React from "react";
import "./wysiwyg.scss";
import {Editor} from "react-draft-wysiwyg";
import {EditorState, Modifier, SelectionState} from "draft-js";
import {convertFromHTML, convertToHTML} from "draft-convert";
import {ObjectCompare, RgbToHex, StringToRgb, UcFirst} from "Functions";
import ToolbarAlign from "./Toolbar/align";
import ToolbarBlockTypes from "./Toolbar/blockType";
import ToolbarColor from "./Toolbar/color";
import ToolbarImage from "./Toolbar/image";
import ToolbarInlines from "./Toolbar/inlines";
import ToolbarLink from "./Toolbar/link";
import ToolbarList from "./Toolbar/list";
import WysiwygDirectionButton from "./Custom/direction";
import WysiwygImageBlock from "./Custom/image";

class Wysiwyg extends React.Component
{
    constructor(props)
    {
        super(props);
        this.Editor = false;
        this.Toolbar =
        {
            options: ["inline", "blockType", "textAlign", "list", "link", "colorPicker"],
            inline: {component: ToolbarInlines},
            blockType: {component: ToolbarBlockTypes},
            textAlign: {component: ToolbarAlign},
            list: {component: ToolbarList},
            link: {component: ToolbarLink},
            colorPicker: {component: ToolbarColor}
        };
        this.ToolbarImage =
        {
            alignmentEnabled: false,
            alt:
            {
                present: false,
                mandatory: false
            },
            component: ToolbarImage,
            inputAccept: "image/gif,image/jpeg,image/jpg,image/png",
            uploadEnabled: false
        };
        this.ToolbarImageIndex = 5;
        this.state =
        {
            alignToolbar: "right",
            direction: "column",
            editorState: EditorState.createEmpty(),
            focus: false,
            html: ""
        };
    }

    componentDidMount()
    {
        const {value} = this.props;
        this.SetContent(value);
        window.addEventListener("resize", this.OnResize);
        this.OnResize();
    }

    componentWillUnmount()
    {
        window.removeEventListener("resize", this.OnResize);
    }

    componentDidUpdate(prevProps)
    {
        const {value: v1} = this.props;
        const {value: v2} = prevProps;
        const v3 = this.GetValue();
        if (!ObjectCompare(v1, v2) && !ObjectCompare(v1, v3))
        {
            this.SetContent(v1);
        }
    }

    AlignToolbar = () =>
    {
        if (!this.Wrapper)
        {
            return;
        }
        const {x, width} = this.Wrapper.getBoundingClientRect();
        const Center = x + width * 2;
        const Align = Center < window.innerWidth / 3 ? "left" : "right";
        if (Align !== this.state.align)
        {
            this.setState({alignToolbar: Align});
        }
    }

    Focus = () =>
    {
        if (!this.Editor)
        {
            return;
        }
        this.Editor.focusEditor();
    }

    GetProps = () =>
    {
        const {direction} = this.state;
        return {direction};
    }

    GetValue = () =>
    {
        const {html} = this.state;
        return [html, this.GetProps()];
    }

    OnBlur = (e) =>
    {
        const {id, onBlur} = this.props;
        const {html} = this.state;
        this.setState({focus: false}, () => onBlur(e, html, id));
    }

    GetContentFromEditorState = (editorState, plainText = false) =>
    {
        const CurrentContent = editorState.getCurrentContent();
        return plainText ? CurrentContent.getPlainText() : convertToHTML({
            blockToHTML: (block) =>
            {
                const Props = {};
                if (block.data["text-align"])
                {
                    Props.align = block.data["text-align"];
                }
                switch (block.type)
                {
                    case "atomic":
                        return <span {...Props}/>;
                    case "blockquote":
                        return <blockquote {...Props}/>;
                    case "code":
                        return <pre {...Props}/>;
                    case "div":
                        return <div {...Props}/>;
                    case "header-one":
                        return <h1 {...Props}/>;
                    case "header-two":
                        return <h2 {...Props}/>;
                    case "header-three":
                        return <h3 {...Props}/>;
                    case "header-four":
                        return <h4 {...Props}/>;
                    case "header-five":
                        return <h5 {...Props}/>;
                    case "header-six":
                        return <h6 {...Props}/>;
                    case "ordered-list-item":
                        return {
                            element: <li {...Props}/>,
                            nest: <ol/>
                        };
                    case "unordered-list-item":
                        return {
                            element: <li {...Props}/>,
                            nest: <ul/>
                        };
                    default:
                        //console.log("Unknown", block.type);
                        return <p {...Props}/>;
                }
            },
            entityToHTML: (entity, originalText) =>
            {
                switch (entity.type)
                {
                    case "IMAGE":
                        const {align = "fill", size = 1, sizeMode = "fill", src, valign = "top"} = entity.data;
                        return <img
                            className="cb-img"
                            data-align={align}
                            data-valign={valign}
                            data-size={size}
                            data-sizemode={sizeMode}
                            src={src}
                        />;
                    case "LINK":
                        const {targetOption, url} = entity.data;
                        const Target = targetOption || "_top";
                        const Rel = Target === "_blank" ? "noopener noreferer" : "";
                        return <a href={url} target={Target} rel={Rel}>{originalText}</a>;
                    default:
                        //console.log("ENTITY", entity, originalText);
                }
                return originalText;
            },
            styleToHTML: (style) =>
            {
                if (style.substr(0, 5) === "color")
                {
                    return <span style={{color: style.substr(6)}}/>;
                }
            }
        })(CurrentContent);
    }

    OnChange = (editorState, callback) =>
    {
        const {delay, minimal} = this.props;
        const {html: previousHtml} = this.state;
        const Html = this.GetContentFromEditorState(editorState, minimal === 2);
        this.setState({
            editorState,
            html: Html
        }, () =>
        {
            if (Html === previousHtml)
            {
                return;
            }
            if (!delay)
            {
                return this.OnChangeReady(editorState, Html, callback);
            }
            clearTimeout(this.UpdateTimeout);
            this.UpdateTimeout = setTimeout(() => this.OnChangeReady(editorState, Html, callback), delay);
        });
    }

    OnChangeReady = (editorState, html, callback) =>
    {
        const {id, onChange} = this.props;
        onChange(editorState, [html, this.GetProps()], id, this.Editor);
        if (typeof callback === "function")
        {
            callback(editorState);
        }
    }

    OnChangeImage = (block, contentState, image) =>
    {
        const {editorState} = this.state;
        const ImageEntityKey = block.getEntityAt(0);
        const UpdatedEntityData = Object.assign(contentState.getEntity(ImageEntityKey).getData(), {src: image.static.large});
        const UpdatedContentState = contentState.replaceEntityData(ImageEntityKey, UpdatedEntityData);
        const UpdatedEditorState = EditorState.set(editorState, {currentContent: UpdatedContentState});
        this.OnChange(UpdatedEditorState, () => this.Focus());
    }

    OnDeleteBlock = (block, contentState) =>
    {
        // <3 https://stackoverflow.com/questions/43991042/how-to-select-and-remove-an-atomic-block
        const BlockKey = block.getKey();
        const BlockText = block.getText();
        const BlockRange = SelectionState.createEmpty(BlockKey).merge({focusOffset: BlockText.length});
        const UpdatedContentState = Modifier.removeRange(contentState, BlockRange, "forward");
        // EditorState.set triggers an error: (Uncaught Invariant Violation: Unknown DraftEntity key: null.)
        // Re-render all contents for now...
        const UpdatedEditorState = EditorState.createWithContent(UpdatedContentState);
        const UpdatedContent = this.GetContentFromEditorState(UpdatedEditorState);
        this.SetContent(UpdatedContent, (editorState, content) =>
        {
            const {id, onChange} = this.props;
            onChange(editorState, [content, this.GetProps()], id, this.Editor);
        });
    }

    OnDirection = (e, direction) =>
    {
        this.setState({direction}, () =>
        {
            const {editorState, html} = this.state;
            const {id, onChange} = this.props;
            onChange(editorState, [html, this.GetProps()], id, this.Editor);
        });
    }

    OnFocus = (e) =>
    {
        const {id, onFocus} = this.props;
        const {html} = this.state;
        this.setState({focus: true}, () => onFocus(e, html, id));
    }

    OnImageHorizontalAlign = (block, contentState, align) =>
    {
        this.SetBlockEntityData(block, contentState, {align});
    }

    OnImageResize = (block, contentState, size) =>
    {
        this.SetBlockEntityData(block, contentState, {size});
    }

    OnImageSizeMode = (block, contentState, sizeMode, size = 1) =>
    {
        this.SetBlockEntityData(block, contentState, {size, sizeMode});
    }

    OnImageVerticalAlign = (block, contentState, valign) =>
    {
        this.SetBlockEntityData(block, contentState, {valign});
    }

    OnResize = () =>
    {
        this.AlignToolbar();
    }

    SetBlockEntityData = (block, contentState, data) =>
    {
        const {editorState} = this.state;
        const ImageEntityKey = block.getEntityAt(0);
        const UpdatedEntityData = Object.assign(contentState.getEntity(ImageEntityKey).getData(), data);
        const UpdatedContentState = contentState.replaceEntityData(ImageEntityKey, UpdatedEntityData);
        // Uhhh... Needs to force re-render.
        const UpdatedEditorState = EditorState.createWithContent(UpdatedContentState);
        //const UpdatedEditorState = EditorState.set(editorState, {currentContent: UpdatedContentState});
        this.OnChange(UpdatedEditorState, () =>
        {
            EditorState.forceSelection(UpdatedEditorState, editorState.getSelection());
            this.Focus();
        });
    }

    RenderBlock = (contentBlock) =>
    {
        const {direction} = this.state;
        switch(contentBlock.getType())
        {
            case "atomic":
                return {
                    component: WysiwygImageBlock,
                    editable: false,
                    props: {
                        contentDirection: direction,
                        onChange: this.OnChangeImage,
                        onDelete: this.OnDeleteBlock,
                        onHorizontalAlign: this.OnImageHorizontalAlign,
                        onResize: this.OnImageResize,
                        onSizeMode: this.OnImageSizeMode,
                        onVerticalAlign: this.OnImageVerticalAlign,
                        wrapper: this.Wrapper
                    }
                };
            default:
        }
    }
    
    Reset = () =>
    {
        const {id, onChange, value} = this.props;
        this.SetContent(value, () =>
        {
            const {editorState} = this.state;
            onChange(editorState, value, id);
        });
    }

    SetContent = (input, callback) =>
    {
        const {minimal} = this.props;
        const [content, props = {}] = Array.isArray(input) ? input : [input];
        const {direction = "column"} = props;
        let SetState;
        if (minimal === 2)
        {
            SetState = content ? EditorState.createWithContent(convertFromHTML(content.replace("\n", "<br/>"))) : EditorState.createEmpty();
        }
        else
        {
            SetState = content ? EditorState.createWithContent(convertFromHTML({
                htmlToBlock: (nodeName, node) =>
                {
                    const Data = {};
                    let Type = "unstyled";
                    if (node.align)
                    {
                        Data["text-align"] = node.align;
                    }
                    switch (nodeName)
                    {
                        case "h1":
                            Type = "header-one";
                            break;
                        case "h2":
                            Type = "header-two";
                            break;
                        case "h3":
                            Type = "header-three";
                            break;
                        case "h4":
                            Type = "header-four";
                            break;
                        case "h5":
                            Type = "header-five";
                            break;
                        case "h6":
                            Type = "header-six";
                            break;
                        case "img":
                            return {type: "atomic", data: {}, text: "img"};
                        case "p":
                            Type = "p";
                            break;
                        case "pre":
                            Type = "code";
                            break;
                        default:
                            return;
                    }
                    return {type: Type, data: Data};
                },
                htmlToEntity: (nodeName, node, createEntity) =>
                {
                    switch (nodeName)
                    {
                        case "a":
                            return createEntity("LINK", "MUTABLE",
                            {
                                url: node.href,
                                targetOption: node.target
                            });
                        case "img":
                            return createEntity("IMAGE", "MUTABLE",
                            {
                                align: node.getAttribute("data-align") || "fill",
                                size: node.getAttribute("data-size") || 1,
                                sizeMode: node.getAttribute("data-sizemode") || "fill",
                                src: node.src,
                                valign: node.getAttribute("data-valign") || "top"
                            })
                        default:
                    }
                },
                htmlToStyle: (nodeName, node, currentStyle) =>
                {
                    const Color = node.style.color;
                    if (Color)
                    {
                        const Hex = RgbToHex(StringToRgb(Color));
                        return currentStyle.add(`color-${Hex}`);
                    }
                    return currentStyle;
                }
            })(content)) : EditorState.createEmpty();
        }
        this.setState({
            direction,
            editorState: SetState,
            html: content
        }, () =>
        {
            if (typeof callback !== "function")
            {
                return;
            }
            callback(SetState, content);
        });
    }

    StyleBlock = (contentBlock) =>
    {
        const {editorState} = this.state;
        const ContentState = editorState.getCurrentContent();
        switch (contentBlock.getType())
        {
            case "atomic":
                const ImageEntityKey = contentBlock.getEntityAt(0);
                const {align = "fill", sizeMode = "fill", valign="center"} = ContentState.getEntity(ImageEntityKey).getData();
                return `align-${align} valign-${valign} sizemode-${sizeMode}`;
            default:
                const textAlign = contentBlock.getData().get("text-align");
                return textAlign ? `rdw-${textAlign}-aligned-block` : "";
        }
    }

    render()
    {
        const {allowMedia, className, disabled, flipDropdowns, minimal, placeholder} = this.props;
        const {alignToolbar, direction, editorState, focus} = this.state;
        const CA = ["Wysiwyg", `Direction${UcFirst(direction)}`, `Toolbar${UcFirst(alignToolbar)}`];
        const ImageIndex = this.Toolbar.options.indexOf("image");
        if (allowMedia && ImageIndex < 0)
        {
            this.Toolbar.image = this.ToolbarImage;
            this.Toolbar.options.splice(this.ToolbarImageIndex, 0, "image")
        }
        else if (!allowMedia && ImageIndex >= 0)
        {
            delete this.Toolbar.image;
            this.Toolbar.options.splice(ImageIndex, 1);
        }
        if (disabled)
        {
            CA.push("Disabled");
        }
        if (focus)
        {
            CA.push("Focus");
        }
        if (minimal === 2)
        {
            CA.push("MinimalLayout NoFormatting");
        }
        else if (minimal)
        {
            CA.push("MinimalLayout");
        }
        else
        {
            CA.push("NormalLayout");
        }
        if (className)
        {
            CA.push(className);
        }
        this.Toolbar.blockType.className = flipDropdowns ? "flip" : "no-flip";
        // The toolbar doesn't re-render automatically, so we need to force it.
        return (
            <div key={direction} ref={wrapper => this.Wrapper = wrapper}>
                <Editor
                    blockRendererFn={this.RenderBlock}
                    blockStyleFn={this.StyleBlock}
                    editorClassName="WysiwygEditor"
                    editorState={editorState}
                    handlePastedText={() => false}
                    onBlur={this.OnBlur}
                    onEditorStateChange={this.OnChange}
                    onFocus={this.OnFocus}
                    placeholder={placeholder}
                    readOnly={disabled}
                    ref={editor => this.Editor = editor}
                    toolbar={this.Toolbar}
                    toolbarClassName="WysiwygToolbar"
                    toolbarCustomButtons={[
                        <WysiwygDirectionButton
                            direction={direction}
                            onChangeDirection={this.OnDirection}
                        />
                    ]}
                    wrapperClassName={CA.join(" ")}
                />
            </div>
        );
    }
}

Wysiwyg.defaultProps =
{
    allowMedia: true,
    className: "",
    delay: 500,
    disabled: false,
    flipDropdowns: false,
    id: "",
    minimal: false,
    onBlur: () => {},
    onChange: () => {},
    onFocus: () => {},
    placeholder: "",
    value: ""
};

export default Wysiwyg;